/**
 * ProgressTracker — Sistema de progresión persistente
 * Trackea nivel, XP, patrones reconocidos, velocidad de diagnóstico
 */

const STORAGE_KEY = 'cgt_progress';

interface ProgressData {
  level: 'novato' | 'estudiante' | 'residente' | 'especialista';
  xp: number;
  casesCompleted: number;
  correctDiagnoses: number;
  totalDiagnoses: number;
  patternsRecognized: string[];
  averageDiagnosisTimeMs: number;
  quizScores: Array<{ caseId: string; score: number; maxScore: number; date: string }>;
  achievements: string[];
}

const LEVEL_THRESHOLDS = {
  novato: 0,
  estudiante: 200,
  residente: 600,
  especialista: 1500,
};

const ACHIEVEMENTS: Record<string, { name: string; description: string; condition: (d: ProgressData) => boolean }> = {
  first_dx: { name: 'Primer Diagnóstico', description: 'Completa tu primer caso', condition: d => d.casesCompleted >= 1 },
  perfect_quiz: { name: 'Quiz Perfecto', description: 'Obtén 100% en un quiz', condition: d => d.quizScores.some(s => s.score === s.maxScore) },
  pattern_5: { name: 'Reconocedor', description: 'Reconoce 5 patrones clínicos', condition: d => d.patternsRecognized.length >= 5 },
  speed_demon: { name: 'Diagnóstico Rápido', description: 'Diagnostica en menos de 30 segundos', condition: d => d.averageDiagnosisTimeMs < 30000 && d.casesCompleted > 0 },
  all_cases: { name: 'Casos Completos', description: 'Completa los 3 casos', condition: d => d.casesCompleted >= 3 },
  ten_cases: { name: 'Veterano', description: 'Completa 10 casos', condition: d => d.casesCompleted >= 10 },
  accuracy_80: { name: 'Precisión Clínica', description: 'Mantén >80% de diagnósticos correctos', condition: d => d.totalDiagnoses >= 3 && (d.correctDiagnoses / d.totalDiagnoses) > 0.8 },
};

export class ProgressTracker {
  private data: ProgressData;
  private hudElement: HTMLElement | null = null;

  constructor() {
    this.data = this.load();
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PERSISTENCIA
  // ═══════════════════════════════════════════════════════════════════════

  private load(): ProgressData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return this.getDefault();
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {}
  }

  private getDefault(): ProgressData {
    return {
      level: 'novato',
      xp: 0,
      casesCompleted: 0,
      correctDiagnoses: 0,
      totalDiagnoses: 0,
      patternsRecognized: [],
      averageDiagnosisTimeMs: 0,
      quizScores: [],
      achievements: [],
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // REGISTRAR EVENTOS
  // ═══════════════════════════════════════════════════════════════════════

  recordDiagnosis(correct: boolean, timeMs: number): void {
    this.data.totalDiagnoses++;
    if (correct) this.data.correctDiagnoses++;

    // Promediar tiempo
    const total = this.data.averageDiagnosisTimeMs * (this.data.totalDiagnoses - 1) + timeMs;
    this.data.averageDiagnosisTimeMs = total / this.data.totalDiagnoses;

    this.addXP(correct ? 50 : 10);
    this.save();
    this.checkAchievements();
    this.updateHUD();
  }

  recordCaseComplete(): void {
    this.data.casesCompleted++;
    this.addXP(30);
    this.save();
    this.checkAchievements();
    this.updateHUD();
  }

  recordQuizScore(caseId: string, score: number, maxScore: number): void {
    this.data.quizScores.push({ caseId, score, maxScore, date: new Date().toISOString() });
    this.addXP(Math.round((score / maxScore) * 80));
    this.save();
    this.checkAchievements();
    this.updateHUD();
  }

  recordPatternRecognized(patternId: string): void {
    if (!this.data.patternsRecognized.includes(patternId)) {
      this.data.patternsRecognized.push(patternId);
      this.addXP(20);
      this.save();
      this.checkAchievements();
      this.updateHUD();
    }
  }

  private addXP(amount: number): void {
    this.data.xp += amount;
    this.updateLevel();
  }

  private updateLevel(): void {
    if (this.data.xp >= LEVEL_THRESHOLDS.especialista) this.data.level = 'especialista';
    else if (this.data.xp >= LEVEL_THRESHOLDS.residente) this.data.level = 'residente';
    else if (this.data.xp >= LEVEL_THRESHOLDS.estudiante) this.data.level = 'estudiante';
    else this.data.level = 'novato';
  }

  private checkAchievements(): void {
    Object.entries(ACHIEVEMENTS).forEach(([id, achievement]) => {
      if (!this.data.achievements.includes(id) && achievement.condition(this.data)) {
        this.data.achievements.push(id);
        this.showAchievementNotification(achievement.name, achievement.description);
      }
    });
    this.save();
  }

  private showAchievementNotification(name: string, description: string): void {
    const notif = document.createElement('div');
    notif.style.cssText = `
      position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
      background: rgba(0,0,0,0.9); border: 1px solid rgba(255,159,10,0.3);
      border-radius: 12px; padding: 14px 24px; z-index: 9000;
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      color: #f5f5f7; text-align: center;
      animation: achIn 0.4s cubic-bezier(0.25,1,0.5,1);
      backdrop-filter: blur(12px);
    `;
    notif.innerHTML = `
      <div style="font-size: 20px; margin-bottom: 4px;">&#x1F3C6;</div>
      <div style="font-size: 14px; font-weight: 700; color: #FF9F0A;">${name}</div>
      <div style="font-size: 11px; color: #a1a1a6;">${description}</div>
    `;
    document.body.appendChild(notif);

    if (!document.getElementById('ach-styles')) {
      const s = document.createElement('style');
      s.id = 'ach-styles';
      s.textContent = `@keyframes achIn { from { opacity:0; transform: translateX(-50%) translateY(-12px); } to { opacity:1; transform: translateX(-50%) translateY(0); } }`;
      document.head.appendChild(s);
    }

    setTimeout(() => {
      notif.style.opacity = '0';
      notif.style.transition = 'opacity 0.5s';
      setTimeout(() => notif.remove(), 500);
    }, 4000);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // HUD
  // ═══════════════════════════════════════════════════════════════════════

  createHUD(): HTMLElement {
    this.hudElement = document.createElement('div');
    this.hudElement.id = 'progress-hud';
    this.hudElement.style.cssText = `
      position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
      background: rgba(0,0,0,0.7); backdrop-filter: blur(12px);
      border: 0.5px solid rgba(255,255,255,0.08);
      border-radius: 10px; padding: 6px 16px;
      z-index: 150; display: flex; align-items: center; gap: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
    `;
    this.updateHUD();
    document.body.appendChild(this.hudElement);
    return this.hudElement;
  }

  private updateHUD(): void {
    if (!this.hudElement) return;

    const levelColors: Record<string, string> = {
      novato: '#6e6e73',
      estudiante: '#2997FF',
      residente: '#FF9F0A',
      especialista: '#30D158',
    };

    const levelLabels: Record<string, string> = {
      novato: 'Novato',
      estudiante: 'Estudiante',
      residente: 'Residente',
      especialista: 'Especialista',
    };

    const nextLevel = this.data.level === 'novato' ? LEVEL_THRESHOLDS.estudiante
      : this.data.level === 'estudiante' ? LEVEL_THRESHOLDS.residente
      : this.data.level === 'residente' ? LEVEL_THRESHOLDS.especialista
      : LEVEL_THRESHOLDS.especialista;

    const prevLevel = this.data.level === 'estudiante' ? LEVEL_THRESHOLDS.novato
      : this.data.level === 'residente' ? LEVEL_THRESHOLDS.estudiante
      : this.data.level === 'especialista' ? LEVEL_THRESHOLDS.residente
      : 0;

    const progress = ((this.data.xp - prevLevel) / (nextLevel - prevLevel)) * 100;
    const color = levelColors[this.data.level];
    const accuracy = this.data.totalDiagnoses > 0
      ? Math.round((this.data.correctDiagnoses / this.data.totalDiagnoses) * 100)
      : 0;

    this.hudElement.innerHTML = `
      <div style="display: flex; align-items: center; gap: 6px;">
        <div style="width: 8px; height: 8px; border-radius: 50%; background: ${color};"></div>
        <span style="font-size: 11px; font-weight: 600; color: ${color};">${levelLabels[this.data.level]}</span>
      </div>
      <div style="width: 60px; height: 4px; background: rgba(255,255,255,0.08); border-radius: 2px; overflow: hidden;">
        <div style="width: ${Math.min(progress, 100)}%; height: 100%; background: ${color}; border-radius: 2px; transition: width 0.5s;"></div>
      </div>
      <span style="font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #6e6e73;">${this.data.xp} XP</span>
      ${this.data.totalDiagnoses > 0 ? `
        <span style="font-size: 10px; color: #6e6e73;">|</span>
        <span style="font-family: 'JetBrains Mono', monospace; font-size: 10px; color: ${accuracy >= 80 ? '#30D158' : accuracy >= 50 ? '#FF9F0A' : '#FF453A'};">${accuracy}% precisión</span>
      ` : ''}
    `;
  }

  getData(): ProgressData { return this.data; }
  reset(): void { this.data = this.getDefault(); this.save(); this.updateHUD(); }
}

export const progressTracker = new ProgressTracker();
export default ProgressTracker;
